import React, { useMemo } from "react";
import {
  FaMapMarkerAlt,
  FaMapPin,
  FaExclamationTriangle,
  FaChartLine,
} from "react-icons/fa";
import AnimatedDisplay from "../../CHARTS/AnimatedDisplay";

const hourBuckets = [
  "wh_less_than_equals_4hr",
  "wh_4_to_6_hr",
  "wh_6_to_8_hr",
  "wh_8_to_9_hr",
  "wh_9_to10_hr",
  "wh_10_to_11_hr",
  "wh_11_to_12_hr",
  "wh_12_to_14_hr",
  "wh_more_than_14hr",
];

const n = (v) => (v == null || v === "" ? 0 : Number(v));

const groupByLobby = (data) => {
  if (!Array.isArray(data)) return [];

  const filtered = data.filter(
    (r) => r && r.wh_lobby && String(r.wh_lobby).toUpperCase() !== "TOTAL"
  );
  const grouped = {};

  filtered.forEach((row) => {
    const lobby = String(row.wh_lobby);
    if (!grouped[lobby]) {
      grouped[lobby] = { wh_lobby: lobby };
      hourBuckets.forEach((k) => (grouped[lobby][k] = 0));
    }
    hourBuckets.forEach((k) => {
      grouped[lobby][k] += n(row[k]);
    });
  });
  return Object.values(grouped);
};

const CMSKpiCards = ({ data }) => {
  const groupedData = useMemo(() => groupByLobby(data), [data]);

  const computed = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) return null;

    let overworked = 0;
    let underworked = 0;

    data.forEach((row) => {
      overworked +=
        n(row.wh_10_to_11_hr) +
        n(row.wh_11_to_12_hr) +
        n(row.wh_12_to_14_hr) +
        n(row.wh_more_than_14hr);

      underworked += n(row.wh_less_than_equals_4hr) + n(row.wh_4_to_6_hr);
    });

    const lobbyTotals = groupedData.map((row) => ({
      lobby: row.wh_lobby,
      total: hourBuckets.reduce((s, k) => s + n(row[k]), 0),
    }));

    const sorted = lobbyTotals.slice().sort((a, b) => b.total - a.total);

    const highest = sorted[0] || { lobby: "-", total: 0 };
    const lowest =
      sorted.length > 0 ? sorted[sorted.length - 1] : { lobby: "-", total: 0 };
    // --- PER LOBBY CALCULATION ---
    const perLobbyStats = groupedData.map((row) => {
      const over =
        n(row.wh_10_to_11_hr) +
        n(row.wh_11_to_12_hr) +
        n(row.wh_12_to_14_hr) +
        n(row.wh_more_than_14hr);

      const under = n(row.wh_less_than_equals_4hr) + n(row.wh_4_to_6_hr);

      return {
        lobby: row.wh_lobby,
        overworked: over,
        underworked: under,
      };
    });

    // --- TOP 3 LOBBIES ---
    const topOverworked = [...perLobbyStats]
      .sort((a, b) => b.overworked - a.overworked)
      .slice(0, 3);

    const topUnderworked = [...perLobbyStats]
      .sort((a, b) => b.underworked - a.underworked)
      .slice(0, 3);

    return {
      highestLobby: highest,
      lowestLobby: lowest,
      overworked,
      underworked,
      topOverworked,
      topUnderworked,
    };
  }, [data, groupedData]);

  // === ONLY 4 CARDS ARE LEFT ===
  const cards = [
    {
      label: "Highest Load Lobby",
      value: computed.highestLobby.lobby,
      sub: `${computed.highestLobby.total}`,
      icon: <FaMapMarkerAlt />,
      color: "#d686aeff",
    },
    {
      label: "Lowest Load Lobby",
      value: computed.lowestLobby.lobby,
      sub: `${computed.lowestLobby.total}`,
      icon: <FaMapPin />,
      color: "#357fc0ff",
    },
    {
      label: "Overworked (10+ hrs)",
      value: computed.overworked,
      sub: computed.topOverworked
        .map((r, i) => `${i + 1}) ${r.lobby} – ${r.overworked}`)
        .join("\n"),
      icon: <FaExclamationTriangle />,
      color: "#e45c5cff",
    },
    {
      label: "Underworked (<6 hrs)",
      value: computed.underworked,
      sub: computed.topUnderworked
        .map((r, i) => `${i + 1}) ${r.lobby} – ${r.underworked}`)
        .join("\n"),
      icon: <FaChartLine />,
      color: "#53b2c5ff",
    },
  ];
  // === ONE SINGLE ROW STYLE (scrollable on small screens) ===
  const gridStyle = {
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
    gap: "18px",
    overflowX: "auto",
    padding: "6px 0",
  };

  return (
    <div className=" newUiBody ps-2 pe-2 mt-2 p-0">
      <div style={gridStyle}>
        {cards.map((card, idx) => (
          <div
            className="newUiBody me-1"
            key={idx}
            style={{
              borderRadius: 12,
              // padding: 16,
              display: "flex",
              gap: 12,
              alignItems: "center",
              fontWeight: "bold",
              minHeight: 84,
              minWidth: 220,
              transition: "transform 160ms ease",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.transform = "translateY(-4px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 28,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: card.color,
                color: "#fff",
                fontSize: 20,
                // marginLeft:"30px"
              }}
            >
              {card.icon}
            </div>

            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, color: "#6c757d" }}>{card.label}</div>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    height: "100%",
                    fontSize: 20,
                    color: "#212529",
                    textAlign: "left",
                  }}
                >
                  <AnimatedDisplay value={card.value} />
                </div>

                {card.sub && (
                  <div
                    style={{
                      fontSize: 13,
                      color: "#6c757d",
                      whiteSpace: "pre-line",
                      marginTop: 6,
                      lineHeight: 1.3,
                      textAlign: "center",
                    }}
                  >
                    {card.sub}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CMSKpiCards;
